import { useState } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  type SeriesConfig,
  type SeriesKind,
  type EmailSubtype,
  SERIES_GROUPS,
  newSeriesId,
  supportsOutcomeFilter,
  supportsRelationshipFilter,
  supportsEmailSubtypeFilter,
} from '@/lib/analytics/seriesConfig';

interface MetricPickerProps {
  series: SeriesConfig[];
  onChange: (next: SeriesConfig[]) => void;
  outcomeOptions: ReadonlyArray<{ value: string; label: string }>;
  relationshipOptions: ReadonlyArray<{ value: string; label: string }>;
}

const EMAIL_SUBTYPE_OPTIONS: ReadonlyArray<{ value: EmailSubtype; label: string }> = [
  { value: 'personal', label: 'Personal' },
  { value: 'campaign', label: 'Campaign' },
];

export function MetricPicker({
  series,
  onChange,
  outcomeOptions,
  relationshipOptions,
}: MetricPickerProps) {
  const findByKind = (kind: SeriesKind) => series.find((s) => s.kind === kind);

  function toggleKind(kind: SeriesKind) {
    const existing = findByKind(kind);
    if (existing) {
      onChange(series.filter((s) => s.id !== existing.id));
    } else {
      onChange([...series, { id: newSeriesId(kind), kind } as SeriesConfig]);
    }
  }

  function setOutcomes(seriesId: string, outcomes: string[]) {
    onChange(
      series.map((s) =>
        s.id === seriesId && s.kind === 'calls'
          ? { ...s, outcomes: outcomes.length > 0 ? outcomes : undefined }
          : s,
      ),
    );
  }

  function setRelationships(seriesId: string, relationships: string[]) {
    onChange(
      series.map((s) =>
        s.id === seriesId && s.kind === 'new_connections'
          ? { ...s, relationships: relationships.length > 0 ? relationships : undefined }
          : s,
      ),
    );
  }

  function setEmailSubtypes(seriesId: string, subtypes: EmailSubtype[]) {
    onChange(
      series.map((s) =>
        s.id === seriesId && s.kind === 'emails'
          ? { ...s, subtypes: subtypes.length > 0 && subtypes.length < 2 ? subtypes : undefined }
          : s,
      ),
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Metrics
          <span className="ml-1.5 text-xs text-muted-foreground">
            ({series.length})
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <div className="space-y-2">
          {SERIES_GROUPS.map((group, gi) => (
            <div key={group.group}>
              {gi > 0 && <Separator className="my-2" />}
              <p className="text-xs font-medium text-muted-foreground px-2 pb-1">
                {group.group}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const existing = findByKind(item.kind);
                  const checked = !!existing;
                  return (
                    <div
                      key={item.kind}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleKind(item.kind)}
                        id={`metric-${item.kind}`}
                      />
                      <label
                        htmlFor={`metric-${item.kind}`}
                        className="text-sm flex-1 cursor-pointer"
                      >
                        {item.label}
                      </label>
                      {checked && supportsOutcomeFilter(item.kind) && (
                        <SubFilterPopover
                          label="Outcomes"
                          options={outcomeOptions}
                          values={existing!.kind === 'calls' ? (existing!.outcomes ?? []) : []}
                          onChange={(v) => setOutcomes(existing!.id, v)}
                        />
                      )}
                      {checked && supportsRelationshipFilter(item.kind) && (
                        <SubFilterPopover
                          label="Types"
                          options={relationshipOptions}
                          values={
                            existing!.kind === 'new_connections'
                              ? (existing!.relationships ?? [])
                              : []
                          }
                          onChange={(v) => setRelationships(existing!.id, v)}
                        />
                      )}
                      {checked && supportsEmailSubtypeFilter(item.kind) && (
                        <SubFilterPopover
                          label="Subtype"
                          options={EMAIL_SUBTYPE_OPTIONS}
                          values={
                            existing!.kind === 'emails' ? (existing!.subtypes ?? []) : []
                          }
                          onChange={(v) =>
                            setEmailSubtypes(existing!.id, v as EmailSubtype[])
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SubFilterPopover({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  function toggle(v: string) {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {label}
          {values.length > 0 && (
            <span className="ml-1 text-muted-foreground">({values.length})</span>
          )}
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-2"
        onClick={(e) => e.stopPropagation()}
      >
        {options.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-1">
            No {label.toLowerCase()} available
          </p>
        ) : (
          <div className="space-y-0.5 max-h-64 overflow-y-auto">
            {options.map((opt) => (
              <div
                key={opt.value}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent"
              >
                <Checkbox
                  checked={values.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                  id={`subfilter-${label}-${opt.value}`}
                />
                <label
                  htmlFor={`subfilter-${label}-${opt.value}`}
                  className="text-sm flex-1 cursor-pointer"
                >
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        )}
        {values.length > 0 && (
          <>
            <Separator className="my-2" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full text-xs"
              onClick={() => onChange([])}
            >
              Clear
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
